import React, { useState, useEffect } from 'react';
import './App.css';
import JoinGrid from './components/JoinGrid/JoinGrid';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { AppBar, Button, Grid, MenuItem, Select, TextField, ThemeProvider, Toolbar, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      light: '#f8bbd0',
      main: '#ec407a',
      dark: '#ad1457',
      contrastText: '#fff',
    },
    secondary: {
      light: '#80d8ff',
      main: '#1565c0',
      dark: '#01579b',
      contrastText: '#000',
    },
  },
});

function App() {
  const [R, setR] = useState(3);
  const [rppR, setRppR] = useState(4);
  const [S, setS] = useState(3);
  const [rppS, setRppS] = useState(4);

  const [cR, setCR] = useState(0);
  const [cS, setCS] = useState(0);
  const [cRR, setCRR] = useState(0);
  const [cSS, setCSS] = useState(-1);

  const [history, setHistory] = useState([]);
  const [speed, setSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  const [IOs, setIOs] = useState(0);
  const [B, setB] = useState(4);

  const [joinType, setJoinType] = useState('SNLJ');
  const joinTypes = ['SNLJ', 'PNLJ', 'BNLJ', 'GHJ'];

  useEffect(() => {
    const interval = setInterval(() => { if (isPlaying) { 
      if (speed > 0) advance();
      else prev();
    } }, (1 / Math.abs(speed)) * 100);
    return () => clearInterval(interval);
  });


  const historyEntry = () => ({
    cR, cS, cRR, cSS, IOs
  })

  const advance = () => {
    let result = [];
    switch(joinType) {
      case 'SNLJ':
          result = advanceSNLJ();
          break;
      case 'PNLJ':
          result = advanceBNLJ(3);
          break;
      case 'BNLJ':
          result = advanceBNLJ(B);
          break;
      default:
        console.error(`Unknown join type ${joinType}`)
    }
    
    if (result !== null) {
      setHistory((old) => [...old, result]);
      setCS(result.cS);
      setCR(result.cR);
      setCSS(result.cSS);
      setCRR(result.cRR);
      setIOs(result.IOs);
    }
  }

  const advanceSNLJ = () => {
    const rv = historyEntry();
    if (rv.cR > R - 1) {
      return null;
    }
    rv.cSS++;
    if (rv.cSS >= rppS) {
      rv.cSS = 0;
      rv.cS++;
      rv.IOs++;

      if (rv.cS >= S) {
        rv.cS = 0;
        rv.cRR ++;

        if (rv.cRR >= rppR) {
          rv.cRR = 0;
          rv.cR ++;
          rv.IOs++;
        }
      }
    }
    if (history.length === 0) {
      rv.IOs = 2;
    }
    return rv;
  }

  const advanceBNLJ = (B) => {
    const rv = historyEntry();
    const currBlockMax = (Math.floor(cR/(B-2))+1)*(B-2)
    if (rv.cSS === rppS-1 && rv.cS === S-1 && rv.cRR === rppR-1 && rv.cR === R-1) {
      return null;
    }
    rv.cSS++;
    // overflow right page
    if (rv.cSS >= rppS) {
      rv.cSS = 0;
      rv.cRR ++;
      // overflow left page
      if (rv.cRR >= rppR) {
        rv.cRR = 0;
        rv.cR++;
        // overflow current block
        if (rv.cR >= R || rv.cR >= currBlockMax) {
          rv.cS++;
          rv.IOs++;
          rv.cR = currBlockMax - (B-2);
          // move to new block
          if (rv.cS >= S) {
            if (currBlockMax + (B-2) <= R-1) {
              rv.IOs += B-2;
            } else {
              rv.IOs += R - currBlockMax;
            }
            rv.cS = 0;
            rv.cR = currBlockMax;
          }
        }
      }
    }
    if (history.length === 0) {
      rv.IOs = Math.min(B-1, R+1);
    }
    return rv;
  }

  const prev = () => {
    if (history.length > 1) {
      const old = history.slice(-2, -1)[0];
      setHistory(prev => prev.slice(0, -1));
      setCR(old.cR);
      setCS(old.cS);
      setCRR(old.cRR);
      setCSS(old.cSS);
      setIOs(old.IOs);
    } else {
      reset();
    }
  }

  const makeSlider = (name, defaultVal, onchange, min, max) => (
    <Slider
      aria-label={name}
      defaultValue={defaultVal}
      valueLabelDisplay="auto"
      onChange={(e) => { reset(); onchange(e.target.value); }}
      step={1}
      marks
      min={min || 1}
      max={max || 5}
    />
  )

  const reset = () => {
    setIsPlaying(false);
    setHistory([]);
    setCR(0);
    setCS(0);
    setCRR(0);
    setCSS(-1);
    setIOs(0);
  }

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <AppBar position="static">
          <Toolbar variant="dense">
            {/* <IconButton edge="start" color="inherit" aria-label="menu" sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton> */}
            <Typography className="heading" variant="h6" color="inherit" component="div">
              CS186 Join Animations
            </Typography>
            <Box sx={{ display: { sm: 'block' } }}>
              {joinTypes.map((item) => (<Button disableElevation color="secondary" variant={(joinType === item) ? "contained" : "text"} key={item} sx={{ color: '#fff' }} onClick={(e) => { setJoinType(item); reset(); }} className="heading">{item}</Button>))}
            </Box>
          </Toolbar>
        </AppBar>

        <Grid container spacing={3} className="controls">
          <Grid item xs={6}>
            Pages in R: {R}{makeSlider("Pages in R", 3, setR)}
            Records per Page in R: {rppR}{makeSlider("Records per Page in R", 4, setRppR, 1, 10)}
          </Grid>
          <Grid item xs={6}>
            Pages in S: {S}{makeSlider("Pages in S", 3, setS)}
            Records per Page in S: {rppS}{makeSlider("Records per Page in S", 4, setRppS, 1, 10)}
          </Grid>


          <Grid item xs={6} className="buttons">
            <Button variant="outlined" onClick={() => { setIsPlaying(false); prev(); }}><ChevronLeftIcon /></Button>
            <Button variant="contained" onClick={() => { setIsPlaying(!isPlaying) }}>{(isPlaying) ? (<PauseIcon />) : (<PlayArrowIcon />)}</Button>
            {/* <Button onClick={() => { setIsPlaying(false) }}><PauseIcon />Pause</Button> */}
            <Button variant="outlined" onClick={() => { setIsPlaying(false); advance(); }}><ChevronRightIcon /></Button>
            <Button variant="text" onClick={() => { reset() }}><DeleteIcon />Reset</Button>
          </Grid>
          <Grid item xs={6} className="buttons">
            <div className="heading">Speed:</div>
              
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={speed}
              label="Speed"
              onChange={(e) => setSpeed(e.target.value)}
            >
              {[-1, 0.1, 0.5, 0.75, 1, 2, 3, 5, 10].map((val) => (<MenuItem value={val}>{val}x</MenuItem>))}
            </Select>
            {joinType === 'BNLJ' && (<span className="B"><div className="heading">B:</div> <TextField type="number" value={B} width={200} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} onChange={(e) => { if (e.target.value > 2) {reset(); setB(e.target.value);} }} /></span>)}
          </Grid>

          <Grid item xs={4}>
            <b>leftRecord:</b> page {cR} record {cRR}
          </Grid>

          <Grid item xs={4}>
            <b>rightRecord:</b> page {cS} record {Math.max(0, cSS)}
          </Grid>
          <Grid item xs={4}>
            <b>IOs:</b> {IOs}
          </Grid>
          
        </Grid>
        {joinType !== 'GHJ' && <div className="joingrid-container">
          <JoinGrid R={R} S={S} rppR={rppR} rppS={rppS} currFilled={history} B={B} joinType={joinType} />
        </div>}
        {joinType === 'GHJ' && <div className="ghj-container">Coming soon!</div>}
      </ThemeProvider>
      </div>
  );
}

export default App;
